import { zodResolver } from '@hookform/resolvers/zod'
import type { BackofficeAuthVerifyBackupCodeData } from '@repo/sdk/gen'
import { backofficeAuthVerifyBackupCode } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { ButtonLink } from '@repo/ui/commons/button-link'
import { Field, FieldError, FieldGroup, FieldLabel } from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import {
	AuthCard,
	AuthCardBody,
	AuthCardHeader
} from '@repo/ui/modules/auth/components/auth-card'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery
} from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { KeySquare } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import i18n, { useTranslation } from '@/lib/i18n'
import {
	adminSessionQueryOptions,
	fetchFreshSession
} from '@/lib/query-options'

const RecoverySchema = z.object({
	code: z.string().min(1, {
		error: () => i18n.t('auth:validation.recovery_code_invalid')
	})
})

export const Route = createFileRoute('/(auth)/two-factor/recovery')({
	component: AdminTwoFactorRecovery
})

function AdminTwoFactorRecovery() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const router = useRouter()
	const queryClient = useQueryClient()
	const { redirect: redirectTo } = Route.useSearch()
	const { data: session } = useSuspenseQuery(adminSessionQueryOptions())
	const mfa = session.sessionType === 'mfa_pending' ? session.mfa : null

	const form = useForm<z.infer<typeof RecoverySchema>>({
		resolver: zodResolver(RecoverySchema),
		defaultValues: { code: '' }
	})

	const verify = useMutation({
		mutationFn: (body: BackofficeAuthVerifyBackupCodeData['body']) =>
			backofficeAuthVerifyBackupCode({ body }),
		onError: () => form.resetField('code'),
		onSuccess: async () => {
			await fetchFreshSession(queryClient)
			// The dashboard loads before the navigation, so it opens ready
			// instead of flashing its loader. A failed preload only costs
			// that: navigation loads the route again.
			await router
				.preloadRoute({ to: redirectTo ?? '/' })
				.catch(() => undefined)
			await navigate({ to: redirectTo ?? '/' })
		}
	})

	if (!mfa) {
		return null
	}

	return (
		<AuthCard>
			<AuthCardHeader
				description={t('auth:two_factor.recovery_description')}
				icon={<KeySquare className='size-8' />}
				title={t('auth:two_factor.title')}
			/>
			<AuthCardBody>
				<form
					onSubmit={form.handleSubmit((data) =>
						verify.mutate({
							mfaToken: mfa.mfaToken,
							code: data.code
						})
					)}
				>
					<FieldGroup>
						{verify.isError && (
							<AuthErrorAlert
								onClose={() => verify.reset()}
								title={formatError(verify.error)}
							/>
						)}
						<Controller
							control={form.control}
							name='code'
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t('auth:two_factor.recovery_label')}
									</FieldLabel>
									<Input
										{...field}
										aria-invalid={fieldState.invalid}
										autoComplete='off'
										autoFocus
										id={field.name}
									/>
									{fieldState.invalid && (
										<FieldError
											errors={[fieldState.error]}
										/>
									)}
								</Field>
							)}
						/>
						<Field>
							<Button disabled={verify.isPending} type='submit'>
								{verify.isPending ? (
									<Spinner />
								) : (
									t('auth:two_factor.verify')
								)}
							</Button>
						</Field>
					</FieldGroup>
				</form>
				<div className='flex justify-center'>
					<ButtonLink
						search={(previous) => previous}
						to='/two-factor/methods'
						variant='link'
					>
						{t('auth:two_factor.use_another_method')}
					</ButtonLink>
				</div>
			</AuthCardBody>
		</AuthCard>
	)
}
